import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { apiUrl } from './config';
import App from './App.tsx';
import './index.css';
import { marcarModoApp, registrarServiceWorker } from './lib/pwa';


import { API_BASE_URL } from '@/config';

// React Query — cliente único para toda la app. Lo consumen módulos como
// bookings-1to1 (useBookings) y business_strategy. Defaults sensatos:
// staleTime 60s para cachear razonablemente, refetchOnWindowFocus off
// para evitar refetch agresivo, retry 1 para resiliencia ante flaps.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Inicializar analytics si es necesario
console.log('Platform initialized with API:', API_BASE_URL);

/* ── Pause all CSS animations & transitions when the tab is hidden ── */
document.addEventListener('visibilitychange', () => {
  document.documentElement.classList.toggle('pause-animations', document.hidden);
});

function setFaviconHref(href: string) {
  const link =
    (document.querySelector("link[rel~='icon']") as HTMLLinkElement | null) ||
    (document.createElement('link') as HTMLLinkElement);
  link.rel = 'icon';
  link.href = href;
  document.head.appendChild(link);
}

// Carga el logo en un <canvas> 64×64 transparente, lo centra
// preservando aspect-ratio, y devuelve un dataURL PNG cuadrado para usar
// como favicon. Evita la distorsión cuando el logo no es cuadrado.
function logoToSquareFavicon(logoUrl: string, size = 64): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('canvas 2d unavailable'));
      ctx.clearRect(0, 0, size, size);
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        // CORS-tainted canvas → fallback al logo crudo (mejor algo a nada)
        resolve(logoUrl);
      }
    };
    img.onerror = () => reject(new Error('logo image failed to load'));
    img.src = logoUrl;
  });
}

async function bootstrap() {
  try {
    const response = await fetch(apiUrl('/org/info'), {
      headers: { 'x-api-key': localStorage.getItem('mcp_api_key') || '' },
    });

    if (!response.ok) throw new Error('No se pudo obtener la info de la organización');

    const { name, logo, favicon } = await response.json();

    if (name) {
      document.title = name;
    }

    // Prioridad: favicon explícito → logo normalizado a cuadrado → fallback /assets/orion.png
    if (favicon) {
      setFaviconHref(favicon);
    } else if (logo) {
      try {
        const square = await logoToSquareFavicon(logo);
        setFaviconHref(square);
      } catch {
        setFaviconHref(logo);
      }
    }
  } catch (err) {
    console.warn('⚠️ No se pudo personalizar favicon o título:', err);
    document.title = 'Dashboard';
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>
  );
}

bootstrap();
marcarModoApp();
registrarServiceWorker();
